import { useState } from "react";
import { MessageCircle, X, Send, Mail } from "lucide-react";
import { Button } from "./ui/button";

const LOGO_URL = "/doslions-logo.png";

// Pre-defined support responses
const SUPPORT_RESPONSES = {
  greeting: `Hi! I'm the ECOLIONS Support Assistant. I can help you with:

• Account information
• Pro Ecopreneur benefits
• Subscription questions

How can I help you today?`,
  
  benefits: `**Pro Ecopreneur Benefits ($49.97 first month, then $99.97/mo):**

• LIONAI Chat - 3 AI-powered market insights per day
• 20+ Trending & Opportunity Stocks
• Extended Commodities & Crypto data
• Real-time Market News Feed
• Weekly AI Trending Picks
• Deep market analysis
• Search any asset worldwide
• Priority support
• Cancel anytime

Ready to upgrade? Visit the Subscription page!`,

  account: `**Your ECOLIONS Account includes:**

**Free Plan:**
• Real-time stock prices (8 stocks)
• Commodity market data (6 commodities)
• Crypto prices (6 cryptos)
• Personal watchlist
• Market search

**Pro Ecopreneur adds:**
• LIONAI Chat (3/day)
• Extended market data
• News feed & AI picks
• Priority support

Check your Profile page for account details!`,

  pricing: `**ECOLIONS Pricing:**

**Free Plan:** $0 forever
• Basic market data
• Personal watchlist
• Market search

**Pro Ecopreneur:** 
• First month: $49.97
• Then: $99.97/month
• Cancel anytime!

**AI Builder Services (one-time):**
• Pro Builder: $99.97
• Expert Builder: $499.97
• Power Builder: $999.97`,

  contact: `For additional assistance or questions I can't answer, please email us directly:

**Email: doslionsteam11@gmail.com**

Our team typically responds within 24-48 hours.

Is there anything else about your account or Pro Ecopreneur benefits I can help with?`,

  default: `I can help you with account information and Pro Ecopreneur benefits.

For other inquiries, please email us directly at:
**doslionsteam11@gmail.com**

Would you like to know about:
• Pro Ecopreneur benefits
• Account features
• Pricing information`
};

// Simple keyword matching for responses
function getResponse(message) {
  const lower = message.toLowerCase();
  
  if (lower.includes("benefit") || lower.includes("pro ecopreneur") || lower.includes("upgrade") || lower.includes("feature")) {
    return SUPPORT_RESPONSES.benefits;
  }
  if (lower.includes("account") || lower.includes("plan") || lower.includes("subscription") || lower.includes("my ")) {
    return SUPPORT_RESPONSES.account;
  }
  if (lower.includes("price") || lower.includes("cost") || lower.includes("how much") || lower.includes("pricing") || lower.includes("$")) {
    return SUPPORT_RESPONSES.pricing;
  }
  if (lower.includes("contact") || lower.includes("email") || lower.includes("human") || lower.includes("support") || lower.includes("help") || lower.includes("issue") || lower.includes("problem") || lower.includes("refund") || lower.includes("cancel")) {
    return SUPPORT_RESPONSES.contact;
  }
  if (lower.includes("hi") || lower.includes("hello") || lower.includes("hey")) {
    return SUPPORT_RESPONSES.greeting;
  }
  
  return SUPPORT_RESPONSES.default;
}

export default function SupportButton() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { type: "bot", text: SUPPORT_RESPONSES.greeting }
  ]);
  const [input, setInput] = useState("");

  const handleSend = () => {
    if (!input.trim()) return;
    
    // Add user message
    const userMessage = input.trim();
    setMessages(prev => [...prev, { type: "user", text: userMessage }]);
    setInput("");
    
    // Get bot response
    setTimeout(() => {
      const response = getResponse(userMessage);
      setMessages(prev => [...prev, { type: "bot", text: response }]);
    }, 500);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      {/* Floating Support Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-20 right-4 z-50 w-12 h-12 bg-amber-500 hover:bg-amber-600 text-black rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110"
        data-testid="support-btn"
        aria-label="Open support chat"
      >
        {isOpen ? (
          <X className="w-5 h-5" />
        ) : (
          <MessageCircle className="w-5 h-5" />
        )}
      </button>

      {/* Support Chat Panel */}
      {isOpen && (
        <div className="fixed bottom-36 right-4 z-50 w-80 sm:w-96 bg-[#111111] border border-amber-500/30 shadow-2xl rounded-lg overflow-hidden">
          {/* Header */}
          <div className="bg-amber-500 px-4 py-3 flex items-center gap-3">
            <img src={LOGO_URL} alt="Support" className="w-8 h-8 object-contain" />
            <div>
              <p className="font-bold text-black text-sm">ECOLIONS Support</p>
              <p className="text-xs text-black/70">Account & Benefits Help</p>
            </div>
          </div>

          {/* Messages */}
          <div className="h-64 overflow-y-auto p-4 space-y-3 bg-[#0A0A0A]">
            {messages.map((msg, index) => (
              <div
                key={index}
                className={`flex ${msg.type === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[85%] px-3 py-2 rounded-lg text-sm whitespace-pre-line ${
                    msg.type === "user"
                      ? "bg-amber-500 text-black"
                      : "bg-zinc-800 text-zinc-200"
                  }`}
                >
                  {msg.text}
                </div>
              </div>
            ))}
          </div>

          {/* Input */}
          <div className="p-3 border-t border-zinc-800 bg-[#111111]">
            <div className="flex gap-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask about accounts or benefits..."
                className="flex-1 bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-amber-500"
                data-testid="support-input"
              />
              <Button
                onClick={handleSend}
                size="sm"
                className="bg-amber-500 hover:bg-amber-600 text-black"
                data-testid="support-send-btn"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-xs text-zinc-600 mt-2 flex items-center gap-1">
              <Mail className="w-3 h-3" />
              Other issues? Email doslionsteam11@gmail.com
            </p>
          </div>
        </div>
      )}
    </>
  );
}
